<template>
  <div>
    <b-alert variant="danger" :show="dataErrors.tooManyObjects">
      <strong>Uwaga.</strong>
      Raporty są dostępne dla maksymalnie 100 obiektów. Ogranicz liczbę
      obiektów, dla których ma być wygenerowany raport lub pobierz dane w
      formacie SHP i wygeneruj odpowiednie raporty w oprogramowaniu GIS.
    </b-alert>
    <b-alert variant="info" :show="true">
      <strong>Informacja.</strong>
      <template
        v-if="formData.dataRange === 'map' || formData.dataRange === 'table'"
      >
        <template>{{ objectCountInfo }}</template>
      </template>
      <template v-else>Liczba wybranych obiektów: -</template>
    </b-alert>
    <span class="h4 text-center d-block mb-3">Wybierz parametry raportu</span>
    <b-form-row>
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.fileFormat"
        >Należy wybrać format pliku.</small
      >
    </b-form-row>
    <b-form-group label="Format pliku" horizontal label-cols="5">
      <b-form-select
        :options="fileFormatsList"
        v-model="formData.fileFormat"
        :class="{ 'is-invalid': dataErrors.fileFormat }"
      ></b-form-select>
    </b-form-group>
    <b-form-row>
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.title"
        >Należy podać tytuł.</small
      >
    </b-form-row>
    <b-form-group label="Tytuł" horizontal label-cols="5">
      <b-form-input
        v-model="formData.title"
        :class="{ 'is-invalid': dataErrors.title }"
      ></b-form-input>
    </b-form-group>
    <b-form-group
      label="Opis"
      horizontal
      label-cols="5"
      v-if="supportedByFileFormat"
    >
      <b-form-textarea v-model="formData.description"></b-form-textarea>
    </b-form-group>
    <b-form-row>
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.dataRange"
        >Należy wybrać zasięg danych.</small
      >
    </b-form-row>
    <b-form-group label="Zasięg danych" horizontal label-cols="5">
      <b-form-select
        :options="dataRangesList"
        v-model="formData.dataRange"
        :class="{ 'is-invalid': dataErrors.dataRange }"
        :disabled="reportWizardLocked || historyTab"
      ></b-form-select>
    </b-form-group>
    <b-form-group
      label="Kolorystyka"
      horizontal
      label-cols="5"
      v-if="supportedByFileFormat"
    >
      <b-form-select
        :options="coloursList"
        v-model="formData.colour"
      ></b-form-select>
    </b-form-group>
    <b-form-row v-if="supportedByFileFormat">
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.clause"
        >Należy wybrać klauzulę.</small
      >
    </b-form-row>
    <b-form-group
      label="Klauzula"
      horizontal
      label-cols="5"
      v-if="supportedByFileFormat"
    >
      <b-form-select
        :options="clausesList"
        v-model="formData.clause"
        :class="{ 'is-invalid': dataErrors.clause }"
        :disabled="clausesFetchInProgress"
      ></b-form-select>
    </b-form-group>
    <b-form-row>
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.coordinateSystem"
        >Należy wybrać układ współrzędnych.</small
      >
    </b-form-row>
    <b-form-group label="Układ współrzędnych" horizontal label-cols="5">
      <b-form-select
        :options="coordinateSystemsList"
        v-model="formData.coordinateSystem"
        :class="{ 'is-invalid': dataErrors.coordinateSystem }"
      ></b-form-select>
    </b-form-group>
    <b-form-row>
      <small
        class="text-danger col-sm-7 offset-sm-5"
        v-show="!reportWizardLocked && dataErrors.geometryFormat"
        >Należy wybrać sposób zapisu danych geometrycznych.</small
      >
    </b-form-row>
    <b-form-group
      label="Sposób zapisu danych geometrycznych"
      horizontal
      label-cols="5"
    >
      <b-form-select
        :options="geometryFormatsList"
        v-model="formData.geometryFormat"
        :class="{ 'is-invalid': dataErrors.geometryFormat }"
      ></b-form-select>
    </b-form-group>
    <b-form-group
      label="Układ strony"
      horizontal
      label-cols="5"
      v-if="supportedByFileFormat"
    >
      <b-form-select
        :options="horizontalLayoutOptions"
        v-model="formData.horizontalLayout"
        :class="{ 'is-invalid': dataErrors.horizontalLayout }"
      ></b-form-select>
    </b-form-group>
    <b-form-group label="Układ" horizontal label-cols="5">
      <b-form-select
        :options="layoutsList"
        v-model="formData.layout"
      ></b-form-select>
    </b-form-group>
    <b-form-group label="Suma częściowa obiektów" horizontal label-cols="5">
      <b-form-select
        :options="attributesList"
        v-model="formData.attribute"
        text-field="name"
        value-field="key"
      ></b-form-select>
    </b-form-group>
    <report-wizard-periodic-generation
      ref="periodicGeneration"
      :formData="formData.periodic"
    ></report-wizard-periodic-generation>
  </div>
</template>

<script>
import ReportWizardPeriodicGeneration from './ReportWizardPeriodicGeneration';
import { mapGetters } from 'vuex';
import { Api as ApiWarstwyWektorowe } from '../../api/api-warstwy-wektorowe';
import { REPORT_LOCKED_SET } from '../../store/mutations.type';

export default {
  name: 'ReportWizardStep2GisData',
  components: {
    ReportWizardPeriodicGeneration
  },
  data: function() {
    return {
      control: null,
      objectsAmount: 0,
      maxCounted: 10000,
      dataErrors: {
        fileFormat: false,
        dataRange: false,
        title: false,
        clause: false,
        coordinateSystem: false,
        horizontalLayout: false,
        geometryFormat: false,
        periodicGeneration: false,
        tooManyObjects: false
      },
      fileFormatsList: [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'txt', value: 'txt' },
        { text: 'csv', value: 'csv' },
        { text: 'rtf', value: 'rtf' },
        { text: 'docx', value: 'docx' },
        { text: 'xlsx', value: 'xlsx' },
        { text: 'xml', value: 'xml' },
        { text: 'pdf', value: 'pdf' }
      ],
      dataRangesList: [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'Tabela atrybutów', value: 'table' },
        { text: 'Zasięg z obszaru okna mapy', value: 'map' }
      ],
      coloursList: [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'Niebieski', value: 'blue' },
        { text: 'Zielony', value: 'green' },
        { text: 'Szary', value: 'gray' }
      ],
      coordinateSystemsList: [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'PL-1992', value: 'EPSG:2180' },
        { text: 'PL-2000 (Strefa V)', value: 'EPSG:2176' },
        { text: 'PL-2000 (Strefa VI)', value: 'EPSG:2177' },
        { text: 'PL-2000 (Strefa VII)', value: 'EPSG:2178' },
        { text: 'PL-2000 (Strefa VIII)', value: 'EPSG:2179' }
      ],
      layoutsList: [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'Wąski', value: 'narrow' },
        { text: 'Standardowy', value: 'standard' },
        { text: 'Szeroki', value: 'wide' }
      ],
      horizontalLayoutOptions: [
        { text: 'Wybierz', value: undefined, disabled: true },
        { text: 'Poziomy', value: true },
        { text: 'Pionowy', value: false }
      ]
    };
  },
  props: {
    formData: Object
  },
  computed: {
    ...mapGetters([
      'featuresLayer',
      'clauses',
      'getViewByIndex',
      'currentViewIndex',
      'clausesFetchInProgress',
      'historyTab',
      'historyTable',
      'reportWizardLocked',
      'selectedFeatures'
    ]),
    attributesList() {
      let attributesList = [{ name: 'Wybierz', key: '', disabled: true }];

      let featuresLayer = this.$store.getters.featuresLayer;

      if (featuresLayer) {
        attributesList = attributesList.concat(featuresLayer.attributes);
      }

      return attributesList;
    },
    supportedByFileFormat() {
      return ['docx', 'rtf', 'pdf'].indexOf(this.formData.fileFormat) != -1;
    },
    clausesList() {
      let list = [{ text: 'Wybierz', value: '', disabled: true }];

      list = list.concat(
        this.clauses.map(x => {
          return { text: x.nazwa, value: x.id };
        })
      );

      return list;
    },
    geometryFormatsList() {
      let list = [
        { text: 'Wybierz', value: '', disabled: true },
        { text: 'WKT', value: 'WKT' },
        { text: 'GeoJSON', value: 'GeoJSON' }
      ];

      if (
        this.featuresLayer &&
        (this.featuresLayer.datasource === 'PUNKTOWA_ISTNIEJACA' ||
          this.featuresLayer.datasource === 'PUNKTOWA_PLANOWANA')
      ) {
        const xy = { text: 'XY', value: 'point_coordinates' };
        list.splice(1, 0, xy);
        this.formData.geometryFormat = 'point_coordinates';
      }

      return list;
    },
    tooManyObjects() {
      return this.objectsAmount > 100;
    },
    objectCountInfo() {
      if (this.objectsAmount >= this.maxCounted + 1) {
        return `Liczba wybranych obiektów: >= ${this.maxCounted + 1}`;
      }

      if (this.objectsAmount <= 100) {
        return `Liczba wybranych obiektów: <= ${100}`;
      }

      var minPower = `${parseInt(this.objectsAmount / 10)}`.length;
      var maxPower = minPower + 1;

      if (Math.pow(10, minPower) == this.objectsAmount) {
        minPower -= 1;
        maxPower -= 1;
      }

      return `Liczba wybranych obiektów: ${Math.pow(10, minPower) +
        1} - ${Math.pow(10, maxPower)}`;
    }
  },
  watch: {
    historyTab(value) {
      if (value) {
        this.dataRangesList.push({
          text: 'Historia obiektów',
          value: 'history'
        });
        this.formData.dataRange = 'history';
      } else {
        this.formData.dataRange = '';
        const drIndexValue = this.dataRangesList.findIndex(
          dr => dr.value == 'history'
        );
        if (drIndexValue >= 0) {
          this.dataRangesList.splice(this.dataRangesList.length - 1, 1);
        }
      }
    },
    'formData.dataRange': async function(newVal) {
      this.$store.commit(REPORT_LOCKED_SET, true);
      this.objectsAmount = await this.getObjectsApproxAmount();
      this.$store.commit(REPORT_LOCKED_SET, false);
    }
  },
  methods: {
    async validate() {
      const self = this;

      self.dataErrors.fileFormat = !self.formData.fileFormat;
      self.dataErrors.title = !self.formData.title;
      self.dataErrors.dataRange = !self.formData.dataRange;
      self.dataErrors.clause =
        self.formData.supportedByFileFormat && !self.formData.clause;
      self.dataErrors.coordinateSystem = !self.formData.coordinateSystem;
      self.dataErrors.horizontalLayout =
        self.formData.horizontalLayout === undefined;
      self.dataErrors.geometryFormat = !self.formData.geometryFormat;
      self.dataErrors.periodicGeneration = !self.$refs.periodicGeneration.validate();
      self.dataErrors.tooManyObjects = this.tooManyObjects;

      return !Object.keys(self.dataErrors).some(x => self.dataErrors[x]);
    },
    getData() {
      let featuresIds = [];
      let range = '';

      if (this.formData.dataRange === 'table') {
        featuresIds = this.selectedFeatures.map(x => x.id);
      } else if (this.formData.dataRange === 'history') {
        featuresIds = this.historyTable.map(x => x.id);
      } else {
        range = this.getViewByIndex(this.currentViewIndex).extent.join(',');
      }

      let data = {
        id_warstwa: this.featuresLayer.id,
        id_obiekty: featuresIds,
        zasieg_widoku: range,
        format_pliku: this.formData.fileFormat,
        tytul: this.formData.title,
        opis: this.formData.description,
        kolorystyka: this.formData.colour,
        klauzula: this.formData.clause,
        uklad_wsp: this.formData.coordinateSystem,
        sposob_zapisu_danych_geom: this.formData.geometryFormat,
        poziomy: this.formData.horizontalLayout,
        uklad_strony: this.formData.layout,
        suma_czesciowa_obiektow: this.formData.attribute,
        aktywny: this.$refs.periodicGeneration.formData.periodicGeneration,
        domyslny_email: this.contextEmail,
        dodatkowe_emaile: this.$refs.periodicGeneration.formData.email
      };

      if (data.aktywny) {
        data.data_zaplanowania = this.$refs.periodicGeneration.dateTime;
        data.cyklicznosc = this.$refs.periodicGeneration.formData.frequency;
        data.liczba_powtorzen = this.$refs.periodicGeneration.formData.repeats;
        data.dodatkowe_emaile = this.$refs.periodicGeneration.formData.email;
      }

      return data;
    },
    reset() {
      const self = this;

      self.formData.fileFormat = 'xlsx';
      self.formData.title = undefined;
      self.formData.description = undefined;

      if (self.formData.dataRange != 'history') {
        self.formData.dataRange = '';
      }

      self.formData.colour = '';
      self.formData.clause = '';
      self.formData.coordinateSystem = 'EPSG:2180';
      self.formData.horizontalLayout = true;
      self.formData.geometryFormat = 'WKT';
      self.formData.layout = 'standard';
      self.formData.attribute = '';

      self.$refs.periodicGeneration.reset();

      Object.keys(self.dataErrors).forEach(x => (self.dataErrors[x] = false));
    },
    async getObjectsApproxAmount() {
      if (this.formData.dataRange === 'table') {
        return this.selectedFeatures.length;
      } else {
        return await this.getObjectsInExtentApproxCount();
      }
    },
    async getObjectsInExtentApproxCount() {
      let control = Date.now().toString();
      this.control = control;
      let layer = this.featuresLayer;

      let coordinates = this.getCoordinatesFromViewExtent();

      var api = new ApiWarstwyWektorowe();

      let body = {
        analiza_filter: {
          geometry_geojson: {
            coordinates: [coordinates],
            type: 'Polygon'
          },
          typ_wejsciowych: 'geometry_geojson',
          typ_analizy: 'zawieraja'
        }
      };

      let response = await api.postWarstwyWektoroweByIdObiektySelekcja({
        id: layer.id,
        body: body,
        tylko_suma: true,
        page_number: 1,
        items_per_page: this.maxCounted + 1
      });
      return response.body.tylko_suma;
    },
    getCoordinatesFromViewExtent() {
      let extent = this.getViewByIndex(this.currentViewIndex).extent;

      let xmin = extent[0];
      let ymin = extent[1];
      let xmax = extent[2];
      let ymax = extent[3];

      return [
        [xmin, ymin],
        [xmax, ymin],
        [xmax, ymax],
        [xmin, ymax],
        [xmin, ymin]
      ];
    }
  }
};
</script>
